import React, { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { useTranslation } from '@wix/cli-i18n';
import { PointerSmallIcon, Key, useInput, Text, Box } from '@wix/cli-ui-kit';
import { PlatformType } from '@wix/cli-app-definitions';
import { useExtensions } from '../extensions/useExtensions.js';
import {
  useOpenPage,
  useResolveBlocksEditorURL,
  useResolveDashboardExtensionDevUrl,
  useResolvePlatformDevUrl,
} from '../extensions/utils.js';
import { useWixCliAppBi } from '../../bi/index.js';
import { usePlatform } from './usePlatform.js';
import { useSite } from '../../site/useSite.js';
import { useEditorInstallation } from '../DevCommand/EditorInstallation.js';
import { useBlocksCLIDevServerProvider } from '../DevCommand/BlocksCLIServerProvider.js';
import { useExtensionData } from '../../context/extensionData.js';

interface PlatformOptionProps {
  inputKey: string;
  label: string;
  suffix?: React.ReactNode;
  disabled?: boolean;
}

const PlatformOption: FC<PlatformOptionProps> = ({
  label,
  inputKey,
  suffix,
  disabled,
}) => {
  const getTextSkin = () => (disabled ? 'secondary' : 'standard');

  return (
    <Box>
      <Box marginRight={1}>
        <Box marginRight={1}>
          <Text skin={getTextSkin()}>
            <PointerSmallIcon /> <Key value={inputKey} /> -
          </Text>
        </Box>
        <Box gap={1}>
          <Text skin={getTextSkin()}>{label}</Text>
        </Box>
      </Box>
      <Text skin={getTextSkin()}>{suffix}</Text>
    </Box>
  );
};

interface PlatformSelector {
  onMultipleDashboardExtensionSelection: () => void;
  onMultipleBackendComponentSelection: () => void;
}

export const PlatformSelector: FC<PlatformSelector> = ({
  onMultipleDashboardExtensionSelection,
}) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const { isPlatformExists } = usePlatform();
  const { openEditor } = useEditorInstallation();
  const { dashboardExtensions } = useExtensions();
  const resolvePlatformUrl = useResolvePlatformDevUrl();
  const resolveDashboardExtensionUrl = useResolveDashboardExtensionDevUrl();
  const resolveBlocksEditorUrl = useResolveBlocksEditorURL();
  const { enabled: isBlocksEnabled } = useBlocksCLIDevServerProvider();
  const { site } = useSite();
  const openPage = useOpenPage();
  const { getDashboardExtensionsRoute } = useExtensionData();

  const openPlatform = useCallback(
    async (platformType: PlatformType) => {
      await openPage({
        title: platformType.type,
        url: resolvePlatformUrl(platformType),
      });
    },
    [openPage, resolvePlatformUrl]
  );

  useInput(async (input) => {
    if (input === 'd' && isPlatformExists(PlatformType.Dashboard())) {
      bi.cliAction({
        question: t('general.view_changes'),
        questionKey: 'general.view_changes',
        action: t('general.platform.dashboard'),
        key: input,
      });
      if (dashboardExtensions.length === 1 && dashboardExtensions[0]) {
        await openPage({
          title: 'Dashboard',
          url: resolveDashboardExtensionUrl(dashboardExtensions[0]),
        });
      } else {
        onMultipleDashboardExtensionSelection();
      }
    }

    if (input === 'e' && isPlatformExists(PlatformType.Editor())) {
      bi.cliAction({
        question: t('general.view_changes'),
        questionKey: 'general.view_changes',
        action: t('general.platform.editor'),
        key: input,
      });

      await openEditor();
    }

    if (
      input === 's' &&
      site.viewUrl &&
      isPlatformExists(PlatformType.Site())
    ) {
      bi.cliAction({
        question: t('general.view_changes'),
        questionKey: 'general.view_changes',
        action: t('general.platform.site'),
        key: input,
      });
      await openPlatform(PlatformType.Site());
    }

    if (input === 'b' && isBlocksEnabled) {
      bi.cliAction({
        question: t('general.view_changes'),
        questionKey: 'general.view_changes',
        action: t('general.platform.blocks_editor'),
        key: input,
      });

      await openPage({
        title: t('general.platform.blocks_editor'),
        url: resolveBlocksEditorUrl(),
      });
    }
    // TODO: Uncomment once https://jira.wixpress.com/browse/FEDINF-12234 is done
    // if (input === 'b' && model.backendComponents.length > 0) {
    //   bi.cliAction({
    //     question: t('general.view_changes'),
    //     questionKey: 'general.view_changes',
    //     action: t('general.platform.backend'),
    //     key: input,
    //   });
    //   onMultipleBackendComponentSelection();
    // }
  });

  const dashboardOptionSuffix = useMemo(() => {
    if (dashboardExtensions.length === 0) {
      return null;
    }
    if (dashboardExtensions.length === 1 && dashboardExtensions[0]) {
      const route = getDashboardExtensionsRoute(dashboardExtensions[0]);
      return <Text skin="secondary"> - {route}</Text>;
    }
    return <Text>{String.fromCodePoint(0x25bc)}</Text>; // ▼
  }, [dashboardExtensions, getDashboardExtensionsRoute]);

  return (
    <Box paddingLeft={2} flexDirection="column">
      {isPlatformExists(PlatformType.Site()) && (
        <PlatformOption
          inputKey="S"
          label={
            site.viewUrl
              ? t('general.platform.site')
              : t('general.platform.unpublished_site')
          }
          disabled={!site.viewUrl}
        />
      )}
      {isPlatformExists(PlatformType.Editor()) && (
        <PlatformOption inputKey="E" label={t('general.platform.editor')} />
      )}
      {isPlatformExists(PlatformType.Dashboard()) && (
        <PlatformOption
          inputKey="D"
          label={t('general.platform.dashboard')}
          suffix={dashboardOptionSuffix}
        />
      )}
      {isBlocksEnabled && (
        <PlatformOption
          inputKey="B"
          label={t('general.platform.blocks_editor')}
        />
      )}
      {/*
      // TODO: Uncomment once https://jira.wixpress.com/browse/FEDINF-12234 is done
      {(hasComponentByType('Event') || hasComponentByType('ServicePlugin')) && (
        <PlatformOption
          inputKey="B"
          label={t('general.platform.backend')}
          suffix={<Text>{String.fromCodePoint(0x25bc)}</Text>} // ▼
        />
      )} */}
    </Box>
  );
};
